"use client";

import type { CommitmentLevel, ContentQueueItem } from "@/lib/api";
import type { TodayPlacement } from "@/lib/api/projection-types";
import type {
  CrossSurfaceConfirmationTruth,
  CrossSurfaceRevealTarget,
} from "@/lib/stores/crossSurfaceConfirmation";
import {
  buildSavedResourceScheduleTitle,
  buildSavedResourceTaskTitle,
  normalizeSavedResourceTitle,
} from "@/lib/savedResourceTitles";
import { formatLocalDateInput, formatTime, parseUTCishDate } from "@/lib/utils";

export function buildContentTaskTitle(item: Pick<ContentQueueItem, "content_type" | "title">): string {
  return buildSavedResourceTaskTitle(item.content_type, item.title);
}

export function buildContentTaskDescription(
  item: Pick<ContentQueueItem, "title" | "url" | "priority_reason" | "goal_title">
): string {
  return [
    `Saved resource: ${normalizeSavedResourceTitle(item.title)}`,
    item.url ? `Link: ${item.url}` : null,
    item.priority_reason ? `Why keep it: ${item.priority_reason}` : null,
    item.goal_title ? `Related goal: ${item.goal_title}` : null,
  ]
    .filter(Boolean)
    .join("\n\n");
}

export function buildContentScheduleTitle(
  item: Pick<ContentQueueItem, "content_type" | "title" | "goal_title">
): string {
  return buildSavedResourceScheduleTitle(item.content_type, item.title, item.goal_title);
}

function roundUpToFiveMinutes(value: Date): Date {
  const next = new Date(value);
  next.setSeconds(0, 0);
  const remainder = next.getMinutes() % 5;
  if (remainder !== 0) {
    next.setMinutes(next.getMinutes() + (5 - remainder));
  }
  return next;
}

function scoreOptionalSlot(start: Date): number {
  const hourValue = start.getHours() + start.getMinutes() / 60;
  if (hourValue >= 14 && hourValue < 19) return 4;
  if (hourValue >= 11 && hourValue < 14) return 3;
  if (hourValue >= 9 && hourValue < 11) return 2;
  if (hourValue >= 19 && hourValue < 20) return 1;
  return 0;
}

export function findNextOpenScheduleSlot(
  blocks: Array<{ start_time: string; end_time: string }>,
  durationMinutes: number,
  targetDate: string
): { start: Date; end: Date } | null {
  const dayStart = new Date(`${targetDate}T08:00:00`);
  const dayEnd = new Date(`${targetDate}T20:00:00`);
  const now = roundUpToFiveMinutes(new Date());
  let cursor = formatLocalDateInput(now) === targetDate && now > dayStart ? now : dayStart;

  const orderedBlocks = [...blocks]
    .map((block) => ({
      start: parseUTCishDate(block.start_time),
      end: parseUTCishDate(block.end_time),
    }))
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const candidateSlots: Array<{ start: Date; end: Date; score: number }> = [];

  const pushCandidate = (start: Date, endBoundary: Date) => {
    const gapMinutes = Math.round((endBoundary.getTime() - start.getTime()) / 60000);
    if (gapMinutes < durationMinutes) return;
    candidateSlots.push({
      start,
      end: new Date(start.getTime() + durationMinutes * 60000),
      score: scoreOptionalSlot(start),
    });
  };

  for (const block of orderedBlocks) {
    if (block.end <= cursor) continue;
    if (block.start > cursor) {
      pushCandidate(cursor, block.start);
    }
    if (block.end > cursor) {
      cursor = roundUpToFiveMinutes(new Date(block.end.getTime() + 5 * 60000));
    }
    if (cursor >= dayEnd) return null;
  }

  pushCandidate(cursor, dayEnd);

  if (candidateSlots.length === 0) return null;

  candidateSlots.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.start.getTime() - b.start.getTime();
  });

  const best = candidateSlots[0];
  return { start: best.start, end: best.end };
}

export function formatCommitmentLabel(value: CommitmentLevel): string {
  return value.replace("_", " ");
}

export function buildPlacementConfirmation(
  placement: TodayPlacement | null,
  messages: {
    restart: string;
    queue: string;
    plan: string;
    fallback: string;
  },
  truthInput?: {
    targetKind: string;
    targetId: string;
    owningService: string;
    proposalId?: string | null;
    authorityLevel?: string | null;
    requiresConfirmation?: boolean | null;
  } | null
): {
  message: string;
  ctaLabel?: string | null;
  ctaTab?: string | null;
  revealTarget?: CrossSurfaceRevealTarget | null;
  truth?: CrossSurfaceConfirmationTruth | null;
} {
  const destinationLayer =
    placement?.location === "plan"
      ? "plan"
      : placement?.location === "restart" ||
          placement?.location === "queue" ||
          placement?.location === "queue_peek"
        ? "today"
        : "today";
  const truth = truthInput
    ? {
        targetKind: truthInput.targetKind,
        targetId: truthInput.targetId,
        destinationLayer,
        owningService: truthInput.owningService,
        proposalId: truthInput.proposalId ?? null,
        authorityLevel: truthInput.authorityLevel ?? null,
        requiresConfirmation: truthInput.requiresConfirmation ?? null,
      }
    : null;

  if (!placement) {
    return { message: messages.fallback, truth };
  }

  if (placement.location === "plan") {
    return {
      message: messages.plan,
      ctaLabel: "Reveal item",
      ctaTab: "schedule-plan",
      revealTarget: placement.object_id
        ? {
            surface: "schedule-plan",
            kind: "schedule_block",
            blockId: placement.object_id,
          }
        : null,
      truth,
    };
  }

  if (placement.location === "queue" || placement.location === "queue_peek") {
    return {
      message: messages.queue,
      ctaLabel: "Reveal item",
      ctaTab: "present",
      revealTarget: placement.object_id
        ? {
            surface: "present",
            kind: "today_queue_item",
            itemId: placement.object_id,
          }
        : null,
      truth,
    };
  }

  if (placement.location === "restart") {
    return {
      message: messages.restart,
      ctaLabel: "Reveal item",
      ctaTab: "present",
      truth,
    };
  }

  return { message: messages.fallback, truth };
}

export { formatTime };
